/*
 * Copyright (c) 2026 Tezi Communications LLP, India
 * All rights reserved. Private and Confidential.
 */

import App from "@frontend/App.tsx";
import { queryClient } from "@frontend/utils/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";

declare global {
  interface Window {
    setLoadingStatus: (message: string, progress: number) => void;
  }
}

// 10% - Initializing engine
window.setLoadingStatus?.('Initializing engine...', 10);

window.setLoadingStatus?.('Syncing your environment...', 25);

// Trigger background sync when browser comes back online
window.addEventListener('online', () => {
    console.info('[Main] Connectivity restored. Triggering sync orchestrator...');
    import("@frontend/worker/worker.proxy")
        .then(async ({ getDataProxy }) => {
          const app = await getDataProxy().sync
          return app.processQueue();
        })
        .catch(err => console.error('[Main] Failed to trigger sync:', err));
});

// Defensive mounting
const container = document.getElementById("root");
if (!container) {
	throw new Error("Root element with id \"root\" not found");
}

const root = createRoot(container, {
  // Simple error logging until Sentry loads
  onUncaughtError: (error, errorInfo) => {
    console.error('Uncaught error:', error, errorInfo.componentStack);
  },
});;

root.render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
				<App />
		</QueryClientProvider>
	</StrictMode>,
);

// Signal that the app is ready and mounted
window.setLoadingStatus?.('Starting application...', 100);

const startDeferredTasks = () => {
  
  // Wait one frame to ensure root.render() has settled visually
  requestAnimationFrame(() => {
    document.body.classList.add('app-mounted');
  });
  
  // Initialize PWA and background tasks after the critical path
  import('./pwa-init').then(m => m.initPWA());

  // Initialize Sentry/Instrumentation lazily
  import('./instrumentation').then(m => m.initInstrumentation());
};

if ('requestIdleCallback' in window) {
  (window as any).requestIdleCallback(() => startDeferredTasks());
} else {
  setTimeout(startDeferredTasks, 2000); // Fallback for browsers without idle support
}
